import { Configuration } from '@parkingbase/ecommerce-api-dev';
import type {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosError,
  AxiosResponse,
  AxiosRequestConfig,
} from 'axios';
import axios, { isAxiosError } from 'axios';
import { DEFAULT_API_TIMEOUT_MS } from '@/lib/api/payment-timeout';

import { installCaptchaInterceptor } from '@/modules/captcha/core';
import { logger } from '@/lib/logger';
import { getAccessTokenExpiry } from '@/lib/token-expiry';
import { isLocationClosedRefusal, readLocationClosedRefusal } from '@/lib/api-error-utils';

/* skipAuth opts a request out of the Authorization header. Declared on both the
 * call-site config (AxiosRequestConfig) and the interceptor config
 * (InternalAxiosRequestConfig). */
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuth?: boolean;
  }
  export interface InternalAxiosRequestConfig {
    skipAuth?: boolean;
  }
}

/** Treat a token this close to expiry as already stale rather than risk it lapsing in flight. */
const TOKEN_EXPIRY_SKEW_MS = 60_000;

/**
 * The refresh-token exchange itself, which travels over this same client and would
 * otherwise deadlock against the refresh it is performing.
 *
 * A backstop, not the primary guard: `loginUsingRefreshToken` marks the call
 * `skipAuth`, which rides with it and so survives this path changing. This catches
 * the other direction — a second caller reaching the endpoint without the marker.
 */
const TOKEN_EXCHANGE_PATH = '/oauth/token';

/** Shared so concurrent requests trigger one exchange instead of a stampede. */
let refreshInFlight: Promise<string | null> | null = null;

type AuthStoreModule = typeof import('@/stores/user.store');

function refreshAccessToken(useAuthStore: AuthStoreModule['useAuthStore']) {
  refreshInFlight ??= (async () => {
    try {
      await useAuthStore.getState().initialize();
      return useAuthStore.getState().token;
    } catch (error) {
      // The refresh token is dead too, so the session is genuinely over. End it
      // rather than leave a signed-in shell over guest data: search resolves
      // prices per account, and showing guest rates under the customer's own
      // avatar is worse than showing them signed out.
      logger.warn('Token refresh failed; continuing unauthenticated', {
        feature: 'api-client',
        error,
      });
      await useAuthStore.getState().logout();
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * The Authorization header to send, exchanging an expired token for a fresh one first.
 *
 * The api rejects an expired bearer outright, including on endpoints that are
 * public and would have succeeded carrying no token at all — so garage search
 * 401'd and rendered as "no parking found" (PB-4648). Sending nothing is
 * strictly better than sending something stale.
 *
 * @returns The header value, or null to send the request unauthenticated.
 */
async function resolveAuthorization(): Promise<string | null> {
  if (typeof window === 'undefined') return null;
  try {
    const { useAuthStore } = await import('@/stores/user.store');
    const { token, refreshToken } = useAuthStore.getState();
    if (!token) return null;

    const expiry = getAccessTokenExpiry(token.split(' ').pop() ?? '');
    // An unreadable expiry is not evidence of staleness — let the server rule on it.
    if (expiry === null || expiry - TOKEN_EXPIRY_SKEW_MS > Date.now()) return token;

    // Nothing to exchange with: an impersonated session carries no refresh token
    // by design (PB-3817). Send what we have rather than silently drop the
    // support user's session.
    if (!refreshToken) return token;

    return await refreshAccessToken(useAuthStore);
  } catch (error) {
    logger.warn('Failed to read token from auth store', {
      feature: 'api-client',
      error,
    });
    return null;
  }
}

/**
 * Creates an Axios instance configured for the server API with request and response interceptors.
 *
 * **Request interceptor:**
 * - **Server:** Sets `X-Current-URL` and `Origin` from Next.js request headers.
 *   Uses pre-set `Authorization` header if available.
 * - **Client:** Sets `X-Current-URL` from `window.location.href` and `Authorization: Bearer`
 *   from the in-memory Zustand auth store, exchanging an expired token for a fresh one
 *   first and sending none at all if that exchange fails (PB-4648).
 *
 * **Response interceptor:**
 * - On 401 Unauthorized (client only): clears auth state and redirects to `/login`.
 *
 * @param config - Optional Axios config (baseURL, timeout, etc.). Use NEXT_PUBLIC_API_URL for baseURL.
 * @returns Configured Axios instance ready for server API calls.
 */
export function createConfiguredApiClient(config?: AxiosRequestConfig): AxiosInstance {
  const axiosInstance = axios.create(config);
  axiosInstance.interceptors.request.use(
    async (requestConfig: InternalAxiosRequestConfig) => {
      if (typeof window === 'undefined') {
        const headerToken = requestConfig.headers.Authorization;
        const { buildRequestUrlFromHeaders, buildOriginFromHeaders } =
          await import('@/lib/api/server-api');
        const { headers } = await import('next/headers');
        const h = await headers();
        const requestUrl = await buildRequestUrlFromHeaders(h);
        const originUrl = await buildOriginFromHeaders(h);
        if (headerToken && !requestConfig.skipAuth) {
          requestConfig.headers.Authorization = `${headerToken}`;
        }
        if (requestUrl) {
          requestConfig.headers['X-Current-URL'] = requestUrl;
        }
        if (originUrl) {
          requestConfig.headers['Origin'] = originUrl;
        }
      } else if (window?.location?.href) {
        requestConfig.headers['X-Current-URL'] = window.location.href;
        const isTokenExchange = requestConfig.url?.includes(TOKEN_EXCHANGE_PATH) ?? false;
        if (!requestConfig.skipAuth && !isTokenExchange) {
          const authorization = await resolveAuthorization();
          if (authorization) {
            requestConfig.headers.Authorization = `${authorization}`;
          }
        }
      }
      return requestConfig;
    },
    (error: unknown) => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      if (typeof window === 'undefined') return Promise.reject(error);

      if (error.response?.status === 401) {
        const { useAuthStore } = await import('@/stores/user.store');
        const authStore = useAuthStore.getState();

        // Only redirect to login if the user was actually authenticated (had a token).
        // Unauthenticated users browsing public pages (e.g. /reserve) may hit 401s
        // for optional API calls — these should fail silently, not force a redirect.
        if (authStore.isAuthenticated || authStore.token) {
          await authStore.logout();
          const { getBasePath, stripCompanyPrefix } = await import('@/lib/base-url-core');
          const basePath = getBasePath(window.location.pathname);
          const loginPath = basePath === '/' ? '/login' : `${basePath}/login`;
          // Carry the page the session expired ON, so re-authenticating returns there instead of
          // the account overview (PB-4971). Without it an expired token mid-scan discards the
          // scan entirely — and the QR code that would let the parker start over is on a sign in
          // the garage, not on their phone.
          //
          // The company prefix is stripped to match every other returnUrl producer: the proxy's
          // `resolvePostLoginTarget` re-adds the base only when the value does not already carry
          // it, so the full path would resolve too, but keeping ONE shape across producers leaves
          // that conditional exercised in one direction only.
          const returnUrl = stripCompanyPrefix(window.location.pathname) + window.location.search;
          window.location.href = `${loginPath}?returnUrl=${encodeURIComponent(returnUrl)}`;
        }
      }
      return Promise.reject(error);
    }
  );

  return axiosInstance;
}

/**
 * Extract a human-readable message from an Axios error response, with a fallback.
 * Prefers `localizedMessage` over `message` from the response body.
 *
 * ## Invariants:
 * - A `GARAGE_CLOSED` refusal (PB-7734) shows the server's `developerMessage` sentence, and the
 *   fallback when that sentence is absent — never the raw machine code. Why: `RestError` carries
 *   no `localizedMessage`, so the generic read below would print `GARAGE_CLOSED`. This is the
 *   reader behind `qr.store.submitPayment`, i.e. the `/z` Pay button (zone tickets and zone
 *   reservations alike) on a page that loaded before the location closed.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (isLocationClosedRefusal(error)) {
    return readLocationClosedRefusal(error) ?? fallback;
  }
  if (isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown> | undefined;
    const msg = data?.localizedMessage ?? data?.message;
    if (typeof msg === 'string' && msg.length > 0) return msg;
  }
  return fallback;
}

const baseURL = process.env.NEXT_PUBLIC_API_URL ?? '';
/**
 * Shared Axios instance configured for the server API.
 * Created once at module load and reused across the application.
 * Includes request/response interceptors for auth (token/cookie) and X-Current-URL.
 * Use this instance for all API calls to ensure consistent behavior.
 */
export const apiClient = createConfiguredApiClient({
  baseURL,
  timeout: DEFAULT_API_TIMEOUT_MS,
});

/* App-wide reCAPTCHA challenge handling. Installed here, on the one shared instance, rather
 * than from a provider: every guarded request goes through this client, and an install step a
 * caller could forget would leave whole screens silently uncovered. The interceptor no-ops
 * during SSR and when no challenge UI is mounted. (PB-3899) */
installCaptchaInterceptor(apiClient);

/**
 * OpenAPI Configuration for @parkingbase/ecommerce-api-dev generated API classes.
 * basePath matches apiClient's baseURL to ensure consistency.
 * Pass this to API class constructors: new SomeApi(apiConfig, undefined, apiClient)
 */
export const apiConfig = new Configuration({
  basePath: baseURL,
});
